'use client';

import { useMergeRefs } from '@floating-ui/react';
import { type ComponentProps, forwardRef, useContext } from 'react';

import { Slot } from '../slot';
import { TriggerButton } from '../trigger-button/trigger-button';
import { PopoverRootContext } from './context/popover-root.context';
import type { PopoverTriggerPropsInterface } from './types/popover-trigger.types';

/**
 * @since 10.0.0
 */
export const PopoverTrigger = forwardRef<HTMLButtonElement, PopoverTriggerPropsInterface>(
  (props: PopoverTriggerPropsInterface, forwardedRef) => {
    const { children = <TriggerButton />, ...rest } = props;
    const { popover } = useContext(PopoverRootContext);
    const mergedRefs = useMergeRefs([popover.refs.setReference, forwardedRef]);
    const mergedReferenceProps = popover.getReferenceProps(rest) as ComponentProps<'button'>;
    const {
      'aria-controls': _ariaControls,
      'aria-expanded': _ariaExpanded,
      'aria-haspopup': _ariaHasPopup,
      ...referenceProps
    } = mergedReferenceProps;

    return (
      <Slot
        ref={mergedRefs}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Popover.Trigger"
        {...referenceProps}
      >
        {children}
      </Slot>
    );
  },
);

PopoverTrigger.displayName = 'Popover.Trigger';
